import React, { useEffect, useState, useCallback } from 'react';
import { Star, ChevronLeft, ChevronRight, ExternalLink, Quote } from 'lucide-react';
import { REVIEWS, GOOGLE_REVIEWS_URL, RATING_ONLY_FALLBACK } from '@/data/reviews';

function Stars({ count = 5 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} size={16} className="text-accent" fill="currentColor" strokeWidth={0} />
      ))}
    </div>
  );
}

export function ReviewsSlideshow({
  intervalMs = 6500,
  eyebrow = 'Client reviews',
  heading = 'What clients say on Google.',
  subheading = 'Authentic 5-star reviews of Fitness Training 360. Read every review on Google or share your own experience.',
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = REVIEWS.length;

  const goTo = useCallback((i) => setIndex(((i % total) + total) % total), [total]);
  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (paused || total <= 1) return undefined;
    const t = setInterval(next, intervalMs);
    return () => clearInterval(t);
  }, [paused, intervalMs, next, total]);

  if (!total) return null;
  const r = REVIEWS[index];

  return (
    <section className="section-sm" data-testid="home-reviews-slideshow">
      <div className="container-x">
        <div className="grid gap-8 lg:grid-cols-12 items-stretch">
          {/* Left: heading + Google CTA */}
          <div className="lg:col-span-4">
            <span className="eyebrow">{eyebrow}</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">{heading}</h2>
            <p className="mt-4 text-[color:var(--ft-muted)] text-lg leading-relaxed">{subheading}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary"
                data-testid="reviews-slideshow-google-cta"
              >
                Read all reviews on Google <ExternalLink size={16} />
              </a>
            </div>
          </div>

          {/* Right: slideshow card */}
          <div className="lg:col-span-8">
            <div
              className="surface relative overflow-hidden p-7 sm:p-9 min-h-[260px] flex flex-col justify-between"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onFocus={() => setPaused(true)}
              onBlur={() => setPaused(false)}
              data-testid="reviews-slideshow-card"
            >
              <Quote
                size={56}
                className="absolute -top-2 -left-2 text-accent opacity-10"
                aria-hidden="true"
              />

              <div className="relative z-10">
                <Stars count={r.rating} />
                <blockquote
                  className="mt-4 font-display text-xl sm:text-2xl leading-snug text-[color:var(--ft-text)]"
                  key={r.id}
                  data-testid={`review-card-${r.id}`}
                >
                  {r.text ? `“${r.text}”` : RATING_ONLY_FALLBACK}
                </blockquote>
                <div className="mt-5">
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-sm text-[color:var(--ft-muted)]">
                    {r.role} · {r.relativeDate} · via {r.source}
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="relative z-10 mt-7 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2" role="tablist" aria-label="Select review">
                  {REVIEWS.map((rev, i) => (
                    <button
                      key={rev.id}
                      type="button"
                      role="tab"
                      aria-selected={i === index}
                      aria-label={`Show review from ${rev.name}`}
                      onClick={() => goTo(i)}
                      data-testid={`reviews-slideshow-dot-${i}`}
                      className={`h-2 rounded-full transition-[width,background-color] duration-200 ${
                        i === index
                          ? 'w-7 bg-accent'
                          : 'w-2 bg-[color:var(--ft-border)] hover:bg-[color:var(--ft-muted)]'
                      }`}
                      style={i === index ? undefined : { transitionProperty: 'width, background-color' }}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={prev}
                    aria-label="Previous review"
                    data-testid="reviews-slideshow-prev"
                    className="btn btn-secondary"
                    style={{ padding: '10px 12px', minHeight: 40 }}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    aria-label="Next review"
                    data-testid="reviews-slideshow-next"
                    className="btn btn-secondary"
                    style={{ padding: '10px 12px', minHeight: 40 }}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ReviewsSlideshow;
